import { memo, useMemo, type ReactNode } from 'react';
import styles from './AuthMurmuration.module.css';

/**
 * The artwork behind the sign-in stage: a murmuration — small chevrons swept
 * along an arc, packed at the crest and fraying at the tails, with one ember
 * cluster at the turn where the eye lands.
 *
 * It is generated rather than drawn because a flock of a thousand hand-placed
 * marks is not a file anyone can edit. The generator is seeded, so the
 * composition is byte-identical on every load — this is a fixed illustration
 * that happens to be computed, not a random one.
 *
 * **It never re-renders.** `memo` with no props means React bails out before
 * reconciling, which matters because the form above it re-renders on every
 * validation and there are ~900 nodes here. It also carries no colour of its
 * own: each bird gets a class, and the stylesheet resolves it to a token, so the
 * flock follows the theme without this file knowing a theme exists.
 */

/** Chevrons per layer, after culling. Enough to read as a flock, few enough to
 *  raster once and never trouble the compositor again. */
const BACK_COUNT = 620;
const FRONT_COUNT = 340;

const BACK_SEED = 20260826;
const FRONT_SEED = 77120493;

const VIEW_W = 1000;
const VIEW_H = 1400;
/** Birds may stray this far outside the frame before they are dropped. */
const CULL_MARGIN = 140;

/** The spine of the flock: a cubic bulging right, both tails running off-frame. */
const SPINE: readonly (readonly [number, number])[] = [
  [380, -180],
  [900, 240],
  [930, 1060],
  [430, 1600],
];

const BIRD_CLASSES = [
  styles.birdTeal,
  styles.birdBlue,
  styles.birdTeal,
  styles.birdBlue,
  styles.birdPale,
] as const;

/** The single chevron every bird is a transformed copy of. */
const CHEVRON = 'M-4.6-2.1L0 2.1 4.6-2.1';

/** A linear congruential generator — small, seedable, and identical everywhere. */
function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return (): number => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

function pointOnSpine(t: number): readonly [number, number] {
  const u = 1 - t;
  const [a, b, c, d] = [u * u * u, 3 * u * u * t, 3 * u * t * t, t * t * t];
  return [
    a * SPINE[0][0] + b * SPINE[1][0] + c * SPINE[2][0] + d * SPINE[3][0],
    a * SPINE[0][1] + b * SPINE[1][1] + c * SPINE[2][1] + d * SPINE[3][1],
  ];
}

function headingAt(t: number): number {
  const step = 0.001;
  const before = pointOnSpine(Math.max(0, t - step));
  const after = pointOnSpine(Math.min(1, t + step));
  return Math.atan2(after[1] - before[1], after[0] - before[0]);
}

/** Three uniforms average into a rough normal deviate, which gives the flock a
 *  soft edge instead of the hard rectangle a flat random spread produces. */
function spreadDeviate(random: () => number): number {
  return (random() + random() + random() - 1.5) * 1.4;
}

interface Bird {
  readonly x: number;
  readonly y: number;
  readonly rotation: number;
  readonly scale: number;
  readonly opacity: number;
  readonly className: string;
}

function buildFlock(seed: number, count: number, scale: number): readonly Bird[] {
  const random = seededRandom(seed);
  const birds: Bird[] = [];

  for (let i = 0; i < count; i += 1) {
    // Bias density toward the crest: a real flock is not evenly spaced.
    const along = (i + random()) / count;
    const t = along + (0.5 - along) * 0.22 * random();
    const crest = Math.sin(Math.PI * Math.min(1, Math.max(0, t)));

    const [spineX, spineY] = pointOnSpine(t);
    const heading = headingAt(t);
    // Tight at the turn, wide at the tails.
    const offset = spreadDeviate(random) * (300 - 170 * crest);
    const x = spineX + Math.cos(heading + Math.PI / 2) * offset;
    const y = spineY + Math.sin(heading + Math.PI / 2) * offset;

    if (x < -CULL_MARGIN || x > VIEW_W + CULL_MARGIN) continue;
    if (y < -CULL_MARGIN || y > VIEW_H + CULL_MARGIN) continue;

    const isEmber = t > 0.26 && t < 0.4 && Math.abs(offset) < 76;
    const birdScale = scale * (0.52 + 0.95 * random() + 0.5 * crest) * (isEmber ? 0.82 : 1);

    birds.push({
      x,
      y,
      rotation: (heading * 180) / Math.PI + 90 + (random() - 0.5) * 22,
      scale: birdScale,
      opacity: Math.min(
        0.95,
        (isEmber ? 0.42 : 0.22) + 0.62 * random() * (0.45 + 0.55 * crest),
      ),
      className: isEmber
        ? styles.birdEmber
        : BIRD_CLASSES[Math.floor(random() * BIRD_CLASSES.length)],
    });
  }

  return birds;
}

function Flock({ birds }: { readonly birds: readonly Bird[] }): ReactNode {
  return (
    <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} preserveAspectRatio="xMidYMid slice" aria-hidden>
      {birds.map((bird, index) => (
        <path
          // The flock is a fixed illustration — nothing is inserted, removed or
          // reordered, so position is a stable identity.
          key={index}
          className={bird.className}
          d={CHEVRON}
          transform={`translate(${bird.x.toFixed(1)} ${bird.y.toFixed(1)}) rotate(${bird.rotation.toFixed(1)}) scale(${bird.scale.toFixed(2)})`}
          // Stroke width is divided out of the scale so every bird draws at the
          // same weight regardless of how large it is.
          strokeWidth={3.6 / bird.scale}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          opacity={bird.opacity.toFixed(2)}
        />
      ))}
    </svg>
  );
}

export const AuthMurmuration = memo(function AuthMurmuration(): ReactNode {
  const back = useMemo(() => buildFlock(BACK_SEED, BACK_COUNT, 1.5), []);
  const front = useMemo(() => buildFlock(FRONT_SEED, FRONT_COUNT, 2.6), []);

  return (
    <>
      <div className={`${styles.layer} ${styles.back}`} aria-hidden>
        <Flock birds={back} />
      </div>
      <div className={`${styles.layer} ${styles.front}`} aria-hidden>
        <Flock birds={front} />
      </div>
    </>
  );
});
